/**
 * Patch Management — operations-first UEM patching console.
 * Lifecycle: Scan → Identify Missing → Approve → Deploy → Confirm → Reboot
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, Device } from '../services/api';
import PatchSettings from './PatchSettings';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from './PageLayout';
import { HubPanel, HubTabs } from './ui/HubShell';
import { DeviceIdentityCell } from './ui/DeviceIdentityCell';
import {
  ShieldCheck,
  RefreshCw,
  Monitor,
  Terminal,
  Laptop,
  Smartphone,
  Apple,
  RotateCcw,
  Package,
  Settings,
  LayoutDashboard,
  ListChecks,
  Server,
  ScrollText,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Download,
  Play,
  Eye,
  Check,
  X,
  Bell,
  Plus,
} from 'lucide-react';
import './PatchManagement.css';

type OsFilter = 'all' | 'windows' | 'linux' | 'macos' | 'android' | 'ios';
type TabId =
  | 'overview'
  | 'required'
  | 'devices'
  | 'deployments'
  | 'reboot'
  | 'catalog'
  | 'settings';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function classifyOs(os?: string): OsFilter | 'other' {
  if (!os) return 'other';
  const l = os.toLowerCase();
  if (l.includes('windows') || l === 'win') return 'windows';
  if (l.includes('linux') || l.includes('ubuntu') || l.includes('debian') || l.includes('rhel') || l.includes('centos') || l.includes('fedora')) return 'linux';
  if (l.includes('mac') || l.includes('darwin')) return 'macos';
  if (l.includes('android')) return 'android';
  if (l.includes('ios') || l.includes('iphone')) return 'ios';
  return 'other';
}

function sourceToOs(source?: string): OsFilter {
  const s = (source || '').toLowerCase();
  if (s === 'msrc') return 'windows';
  if (s === 'apt' || s === 'yum' || s === 'dnf') return 'linux';
  if (s === 'apple') return 'macos';
  return 'all';
}

function isCritical(sev?: string) {
  return (sev || '').toLowerCase() === 'critical';
}

function isUuid(id?: string) {
  return !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

function SeverityBadge({ severity }: { severity?: string }) {
  const s = (severity || 'Unknown').trim() || 'Unknown';
  const key = s.toLowerCase();
  const cls =
    key === 'critical' ? 'pm-badge pm-badge-critical' :
    key === 'important' || key === 'high' ? 'pm-badge pm-badge-important' :
    key === 'moderate' || key === 'medium' ? 'pm-badge pm-badge-moderate' :
    key === 'low' ? 'pm-badge pm-badge-low' :
    'pm-badge pm-badge-neutral';
  return <span className={cls}>{s}</span>;
}

function ApprovalBadge({ status }: { status?: string }) {
  const s = status || 'Not_Set';
  const cls =
    s === 'Approved' ? 'pm-badge pm-badge-ok' :
    s === 'Declined' ? 'pm-badge pm-badge-fail' :
    'pm-badge pm-badge-warn';
  return <span className={cls}>{s.replace('_', ' ')}</span>;
}

function StatusBadge({ status }: { status?: string }) {
  const s = (status || 'unknown').toLowerCase();
  const cls =
    s.includes('install') && !s.includes('partial') && !s.includes('fail') ? 'pm-badge pm-badge-ok' :
    s.includes('fail') || s.includes('error') ? 'pm-badge pm-badge-fail' :
    s.includes('reboot') || s.includes('await') || s.includes('running') || s.includes('queued') || s.includes('partial') ? 'pm-badge pm-badge-warn' :
    'pm-badge pm-badge-neutral';
  return <span className={cls}>{status || '—'}</span>;
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="pm-empty">
      <ShieldCheck size={36} className="pm-empty-icon" />
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'required', label: 'Required Patches', icon: ListChecks },
  { id: 'devices', label: 'Devices Needing Patch', icon: Server },
  { id: 'deployments', label: 'Deployments', icon: ScrollText },
  { id: 'reboot', label: 'Reboot', icon: RotateCcw },
  { id: 'catalog', label: 'Patch Catalog', icon: BookOpen },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const OS_PILLS: { id: OsFilter; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All', icon: Package },
  { id: 'windows', label: 'Windows', icon: Monitor },
  { id: 'linux', label: 'Linux', icon: Terminal },
  { id: 'macos', label: 'macOS', icon: Laptop },
  { id: 'android', label: 'Android', icon: Smartphone },
  { id: 'ios', label: 'iOS', icon: Apple },
];

const PatchManagement: React.FC = () => {
  const { can, canAny, osEnabled } = useAuth();
  // TODO: tighten when permission catalog is fully assigned to all roles
  const canApprove = can('patches.all.approve') || canAny(['patches.all.approve']);
  const canInstall = can('patches.all.install') || canAny(['patches.windows.install', 'patches.linux.install', 'patches.macos.install', 'patches.all.install']);
  const canSettings = can('patches.all.settings_edit');
  const canRebootManage = can('reboot.all.manage') || can('reboot.all.view');
  const canForceReboot = can('reboot.all.force');

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [osFilter, setOsFilter] = useState<OsFilter>('all');
  const [devices, setDevices] = useState<Device[]>([]);
  const [requiredPatches, setRequiredPatches] = useState<any[]>([]);
  const [deployments, setDeployments] = useState<any[]>([]);
  const [rebootSummary, setRebootSummary] = useState<{ pending_count: number; items: any[] } | null>(null);
  const [rebootPolicies, setRebootPolicies] = useState<any[]>([]);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [selectedPatchIds, setSelectedPatchIds] = useState<Set<string>>(new Set());
  const [drawerPatch, setDrawerPatch] = useState<any | null>(null);
  const [drawerDevices, setDrawerDevices] = useState<any[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [deviceRollup, setDeviceRollup] = useState<Record<string, { missing: number; critical: number; failed: number; pendingReboot: number; lastScan?: string }>>({});
  const [selectedDeviceDetail, setSelectedDeviceDetail] = useState<Device | null>(null);
  const [deviceStatusItems, setDeviceStatusItems] = useState<any[]>([]);
  const [deviceStatusLoading, setDeviceStatusLoading] = useState(false);
  const [scanningDevices, setScanningDevices] = useState(false);

  const [patchMasterData, setPatchMasterData] = useState<any>(null);
  const [patchMasterLoading, setPatchMasterLoading] = useState(false);
  const [patchMasterError, setPatchMasterError] = useState<string | null>(null);
  const [patchMasterSource, setPatchMasterSource] = useState('msrc');
  const [patchMasterSeverity, setPatchMasterSeverity] = useState('');
  const [patchMasterYear, setPatchMasterYear] = useState<number | ''>(new Date().getFullYear());
  const [patchMasterMonth, setPatchMasterMonth] = useState<number | ''>(new Date().getMonth() + 1);
  const [patchMasterSearch, setPatchMasterSearch] = useState('');
  const [syncing, setSyncing] = useState(false);

  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({ name: '', type: 'Prompt', config: {} as Record<string, any> });
  const [policySubmitLoading, setPolicySubmitLoading] = useState(false);
  const [busyPatchId, setBusyPatchId] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 6000);
  };

  const filteredDevices = useMemo(() => {
    if (osFilter === 'all') return devices;
    return devices.filter((d) => classifyOs(d.os) === osFilter);
  }, [devices, osFilter]);

  const filteredRequired = useMemo(() => {
    if (osFilter === 'all') return requiredPatches;
    return requiredPatches.filter((r) => sourceToOs(r.source) === osFilter || sourceToOs(r.source) === 'all');
  }, [requiredPatches, osFilter]);

  const osCounts = useMemo(() => {
    const counts: Record<OsFilter, number> = { all: devices.length, windows: 0, linux: 0, macos: 0, android: 0, ios: 0 };
    devices.forEach((d) => {
      const c = classifyOs(d.os);
      if (c !== 'other') counts[c] += 1;
    });
    return counts;
  }, [devices]);

  const devicesMissingCount = useMemo(() => {
    return filteredDevices.filter((d) => (deviceRollup[d.device_id]?.missing || 0) > 0 || (Array.isArray(d.device_metadata?.patch_reports) && d.device_metadata.patch_reports.length > 0)).length;
  }, [filteredDevices, deviceRollup]);

  const criticalMissing = useMemo(
    () => filteredRequired.filter((r) => isCritical(r.severity)).reduce((n, r) => n + (Number(r.affected_devices) || 0), 0) ||
      filteredRequired.filter((r) => isCritical(r.severity)).length,
    [filteredRequired]
  );

  const failedInstalls = useMemo(
    () => deployments.reduce((n, j) => n + (Number(j.failed_count) || 0), 0),
    [deployments]
  );

  const pendingReboot = useMemo(() => {
    const fromRollup = filteredDevices.reduce((n, d) => n + (deviceRollup[d.device_id]?.pendingReboot || 0), 0);
    if (fromRollup > 0) return fromRollup;
    return rebootSummary?.pending_count ?? 0;
  }, [filteredDevices, deviceRollup, rebootSummary]);

  const devicesNeverScanned = useMemo(
    () => filteredDevices.filter((d) => !deviceRollup[d.device_id]?.lastScan).length,
    [filteredDevices, deviceRollup]
  );

  const compliancePct = useMemo(() => {
    const total = filteredDevices.length;
    if (!total) return 100;
    // Do not claim 100% when agents have never reported a patch scan.
    if (devicesNeverScanned === total) return 0;
    const scanned = total - devicesNeverScanned;
    const missing = devicesMissingCount;
    const denom = scanned > 0 ? scanned : total;
    return Math.max(0, Math.min(100, Math.round(((denom - missing) / denom) * 100)));
  }, [filteredDevices.length, devicesMissingCount, devicesNeverScanned]);

  const handleScanDevices = async () => {
    const targets = filteredDevices.slice(0, 40);
    if (!targets.length) {
      showToast('No devices in the current filter to scan.');
      return;
    }
    setScanningDevices(true);
    try {
      let ok = 0;
      let fail = 0;
      await Promise.all(
        targets.map(async (d) => {
          try {
            await apiService.triggerDevicePatchScan(d.device_id);
            ok += 1;
          } catch {
            fail += 1;
          }
        })
      );
      showToast(
        fail
          ? `Queued patch scan on ${ok} device(s); ${fail} failed. Refresh in ~1–2 minutes.`
          : `Queued patch scan on ${ok} device(s). Refresh in ~1–2 minutes after the agent reports.`
      );
    } finally {
      setScanningDevices(false);
    }
  };

  const loadAll = useCallback(async (soft = false) => {
    if (soft) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const osParam = osFilter === 'all' ? undefined : osFilter;
      const [devs, req, deps, reboot, sync] = await Promise.all([
        apiService.getDevices().catch(() => []),
        apiService.getRequiredPatches({ os: osParam, limit: 500 }).catch(() => null),
        apiService.getPatchDeployments(100).catch(() => null),
        apiService.getRebootStatusSummary().catch(() => null),
        apiService.getPatchSyncStatus().catch(() => null),
      ]);
      const deviceList = Array.isArray(devs) ? devs : [];
      setDevices(deviceList);
      setRequiredPatches(req?.data?.items || req?.items || []);
      setDeployments(deps?.data?.items || deps?.items || []);
      setRebootSummary(reboot?.data || { pending_count: 0, items: [] });
      setSyncStatus(sync?.data || sync);

      // Device rollup: sample status for devices with telemetry or all filtered (cap 40)
      const candidates = deviceList
        .filter((d) => osFilter === 'all' || classifyOs(d.os) === osFilter)
        .slice(0, 40);
      const roll: typeof deviceRollup = {};
      await Promise.all(
        candidates.map(async (d) => {
          try {
            const res = await apiService.getDevicePatchStatus(d.device_id);
            const items = res?.data?.items || res?.items || [];
            const missing = items.filter((i: any) => i.status === 'Missing').length;
            const critical = items.filter((i: any) => i.status === 'Missing' && isCritical(i.patch?.severity)).length;
            const failed = items.filter((i: any) => i.status === 'Failed').length;
            const pendingRebootN = items.filter((i: any) => i.status === 'Pending_Reboot').length;
            const lastScanFromItems = items.map((i: any) => i.last_scan_date).filter(Boolean).sort().reverse()[0];
            const lastScan =
              lastScanFromItems ||
              res?.data?.last_patch_scan_at ||
              d.device_metadata?.last_patch_scan_at ||
              undefined;
            // Fallback to telemetry if DPS empty
            const reports = Array.isArray(d.device_metadata?.patch_reports) ? d.device_metadata.patch_reports : [];
            const metaMissing = Number(d.device_metadata?.last_patch_scan_missing_count);
            roll[d.device_id] = {
              missing: missing || reports.length || (Number.isFinite(metaMissing) ? metaMissing : 0),
              critical: critical || reports.filter((r: any) => isCritical(r.severity)).length,
              failed,
              pendingReboot: pendingRebootN || (d.device_metadata?.last_patch_scan_reboot_required ? 1 : 0),
              lastScan,
            };
          } catch {
            const reports = Array.isArray(d.device_metadata?.patch_reports) ? d.device_metadata.patch_reports : [];
            roll[d.device_id] = {
              missing: reports.length,
              critical: reports.filter((r: any) => isCritical(r.severity)).length,
              failed: 0,
              pendingReboot: 0,
            };
          }
        })
      );
      setDeviceRollup(roll);

      try {
        const pol = await apiService.getRebootPolicies();
        setRebootPolicies(pol?.data?.policies || []);
      } catch {
        setRebootPolicies([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load patch management data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [osFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!selectedDeviceDetail?.device_id) {
      setDeviceStatusItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setDeviceStatusLoading(true);
      try {
        const res = await apiService.getDevicePatchStatus(selectedDeviceDetail.device_id);
        if (!cancelled) setDeviceStatusItems(res?.data?.items || res?.items || []);
      } catch {
        if (!cancelled) setDeviceStatusItems([]);
      } finally {
        if (!cancelled) setDeviceStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDeviceDetail?.device_id]);

  const handleApprove = async (patchId: string, status: 'Approved' | 'Declined' | 'Not_Set') => {
    if (!canApprove) {
      showToast('You do not have permission to approve patches.');
      return;
    }
    setBusyPatchId(patchId);
    try {
      await apiService.setPatchApproval(patchId, '', status);
      setRequiredPatches((prev) => prev.map((p) => (p.patch_id === patchId ? { ...p, approval_status: status } : p)));
      showToast(`Patch ${status === 'Approved' ? 'approved' : status === 'Declined' ? 'declined' : 'reset'}.`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Approval failed');
    } finally {
      setBusyPatchId(null);
    }
  };

  const handleDeploy = async (patchId: string, deviceIds?: string[]) => {
    if (!canInstall) {
      showToast('You do not have permission to install patches.');
      return;
    }
    if (!isUuid(patchId)) {
      showToast('This catalog entry is not installable (ephemeral / missing patch_master UUID).');
      return;
    }
    setBusyPatchId(patchId);
    try {
      let targets: Device[] =
        deviceIds && deviceIds.length
          ? devices.filter((d) => deviceIds.includes(d.device_id))
          : [];

      // Prefer devices that actually report this patch as Missing
      if (!targets.length) {
        const pool = filteredDevices.slice(0, 40);
        const matched: Device[] = [];
        await Promise.all(
          pool.map(async (d) => {
            try {
              const res = await apiService.getDevicePatchStatus(d.device_id, 'Missing');
              const items = res?.data?.items || res?.items || [];
              if (items.some((i: any) => i.patch_id === patchId)) matched.push(d);
            } catch {
              /* ignore */
            }
          })
        );
        targets = matched.slice(0, 5);
      }

      if (!targets.length) {
        showToast('No active device currently reports this patch as Missing. Open View Devices or wait for agent scan.');
        return;
      }

      let lastJob: string | undefined;
      for (const t of targets) {
        const res = await apiService.triggerPatchInstall(t.device_id, [patchId]);
        lastJob = res?.data?.job_id || res?.job_id || lastJob;
      }
      showToast(lastJob ? `Deployment queued (job ${String(lastJob).slice(0, 8)}…).` : 'Deployment queued.');
      setActiveTab('deployments');
      loadAll(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Deploy failed');
    } finally {
      setBusyPatchId(null);
    }
  };

  const handleBulkApprove = async () => {
    for (const id of Array.from(selectedPatchIds)) {
      await handleApprove(id, 'Approved');
    }
    setSelectedPatchIds(new Set());
  };

  const handleBulkDeploy = async () => {
    for (const id of Array.from(selectedPatchIds)) {
      await handleDeploy(id);
    }
    setSelectedPatchIds(new Set());
  };

  const openAffectedDevices = async (patch: any) => {
    setDrawerPatch(patch);
    setDrawerLoading(true);
    setDrawerDevices([]);
    try {
      const os = sourceToOs(patch.source);
      const pool = devices.filter((d) => os === 'all' || classifyOs(d.os) === os).slice(0, 50);
      const rows: any[] = [];
      await Promise.all(
        pool.map(async (d) => {
          try {
            const res = await apiService.getDevicePatchStatus(d.device_id);
            const items = res?.data?.items || res?.items || [];
            const hit = items.find((i: any) => i.patch_id === patch.patch_id && ['Missing', 'Pending_Reboot', 'Failed'].includes(i.status));
            if (hit) {
              rows.push({
                device: d,
                status: hit.status,
                lastScan: hit.last_scan_date,
                reboot: hit.status === 'Pending_Reboot',
              });
            } else if ((deviceRollup[d.device_id]?.missing || 0) > 0 && os !== 'all') {
              // soft include devices with missing of same OS when exact match not found
            }
          } catch {
            /* ignore */
          }
        })
      );
      setDrawerDevices(rows);
    } finally {
      setDrawerLoading(false);
    }
  };

  const loadPatchMaster = async () => {
    setPatchMasterLoading(true);
    setPatchMasterError(null);
    try {
      const res = await apiService.getPatchMaster({
        source: patchMasterSource || undefined,
        severity: patchMasterSeverity || undefined,
        year: patchMasterYear || undefined,
        month: patchMasterMonth || undefined,
        limit: 100,
      });
      setPatchMasterData(res?.data || res);
    } catch (e) {
      setPatchMasterError(e instanceof Error ? e.message : 'Failed to load catalog');
      setPatchMasterData(null);
    } finally {
      setPatchMasterLoading(false);
    }
  };

  const handleSyncCatalog = async () => {
    setSyncing(true);
    try {
      const res = await apiService.triggerPatchSync();
      setSyncStatus(res?.data || res);
      showToast('Catalog sync started / completed.');
      loadAll(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreatePolicy = async () => {
    setPolicySubmitLoading(true);
    try {
      await apiService.createRebootPolicy({
        name: policyForm.name,
        type: policyForm.type,
        config: policyForm.config,
        is_active: true,
      });
      setPolicyModalOpen(false);
      setPolicyForm({ name: '', type: 'Prompt', config: {} });
      const pol = await apiService.getRebootPolicies();
      setRebootPolicies(pol?.data?.policies || []);
      showToast('Reboot policy created.');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to create policy');
    } finally {
      setPolicySubmitLoading(false);
    }
  };

  const stepper = useMemo(() => {
    const notSet = filteredRequired.filter((r) => (r.approval_status || 'Not_Set') === 'Not_Set').length;
    const approved = filteredRequired.filter((r) => r.approval_status === 'Approved').length;
    const running = deployments.filter((j) => ['Running', 'Queued', 'Awaiting_Confirmation'].includes(j.status)).length;
    const failed = deployments.filter((j) => j.status === 'Failed' || (j.failed_count || 0) > 0).length;
    const installedJobs = deployments.filter((j) => j.status === 'Installed').length;
    const anyAgentScan = filteredDevices.some((d) => !!deviceRollup[d.device_id]?.lastScan);
    return [
      {
        label: 'Scan',
        status: anyAgentScan
          ? 'Completed'
          : scanningDevices
            ? 'In Progress'
            : devicesNeverScanned
              ? 'Never scanned'
              : 'Pending',
        tone: anyAgentScan ? 'ok' : 'warn',
      },
      {
        label: 'Review Missing',
        status: filteredRequired.length ? `${filteredRequired.length} pending` : anyAgentScan ? 'Completed' : 'Waiting for scan',
        tone: filteredRequired.length ? 'warn' : anyAgentScan ? 'ok' : 'warn',
      },
      {
        label: 'Approve',
        status: notSet ? `${notSet} Not Set` : approved ? 'Completed' : 'Pending',
        tone: notSet ? 'warn' : 'ok',
      },
      {
        label: 'Deploy',
        status: running ? 'In Progress' : deployments.length ? 'Completed' : 'Pending',
        tone: running ? 'progress' : undefined,
      },
      {
        label: 'Confirm Installed',
        status: failed ? 'Failed' : installedJobs ? 'Completed' : running ? 'In Progress' : 'Pending',
        tone: failed ? 'fail' : undefined,
      },
      {
        label: 'Reboot',
        status: pendingReboot ? 'Required' : 'Completed',
        tone: pendingReboot ? 'warn' : 'ok',
      },
    ];
  }, [filteredRequired, deployments, pendingReboot, filteredDevices, deviceRollup, scanningDevices, devicesNeverScanned]);

  const catalogItems = useMemo(() => {
    const items = patchMasterData?.items || patchMasterData?.patches || (Array.isArray(patchMasterData) ? patchMasterData : []);
    if (!patchMasterSearch.trim()) return items;
    const q = patchMasterSearch.toLowerCase();
    return items.filter((p: any) =>
      [p.kb_id, p.package_name, p.description, ...(p.cve_ids || [])].join(' ').toLowerCase().includes(q)
    );
  }, [patchMasterData, patchMasterSearch]);

  const headerActions = (
    <div className="pm-header-actions">
      <button type="button" className="pm-btn pm-btn-ghost" onClick={() => loadAll(true)} disabled={refreshing || loading}>
        <RefreshCw size={16} className={refreshing ? 'pm-spin' : ''} />
        Refresh
      </button>
      <button
        type="button"
        className="pm-btn pm-btn-ghost"
        onClick={handleScanDevices}
        disabled={scanningDevices || loading || !filteredDevices.length}
        title="Queue an immediate Windows Update / OS patch scan on filtered devices"
      >
        <RefreshCw size={16} className={scanningDevices ? 'pm-spin' : ''} />
        Scan Devices
      </button>
      <button type="button" className="pm-btn pm-btn-ghost" onClick={handleSyncCatalog} disabled={syncing}>
        <Download size={16} className={syncing ? 'pm-spin' : ''} />
        Sync Catalog
      </button>
      <button
        type="button"
        className="pm-btn pm-btn-primary"
        onClick={() => {
          setActiveTab('required');
          showToast('Select patches below, then use Deploy Selected or Deploy on a row.');
        }}
        disabled={!canInstall}
        title={!canInstall ? 'Install permission required' : undefined}
      >
        <Play size={16} />
        Create Deployment
      </button>
      <button
        type="button"
        className="pm-btn pm-btn-ghost"
        onClick={() => setActiveTab('settings')}
        disabled={!canSettings && false /* soft: still allow open; server enforces save */}
      >
        <Settings size={16} />
        Patch Settings
      </button>
    </div>
  );

  if (loading && !devices.length) {
    return (
      <div className="patch-management pm-ops">
        <div className="pm-loading">Loading patch operations…</div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Patch Management"
      subtitle="Identify missing patches, deploy approved updates, and confirm installation status across endpoints."
      icon={<ShieldCheck size={20} />}
      eyebrow="Fleet"
      action={headerActions}
    >
      <div className="patch-management pm-ops">
        {error && (
          <div className="pm-error-banner">
            {error}
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}
        {toast && <div className="pm-toast">{toast}</div>}

        {/* KPI cards */}
        <div className="pm-kpi-grid">
          {[
            { label: 'Total Devices', value: filteredDevices.length, icon: Server, hint: osFilter === 'all' ? 'All platforms' : osFilter, tone: 'blue' },
            { label: 'Devices Missing Patches', value: devicesMissingCount, icon: AlertTriangle, hint: 'Need updates', tone: 'amber' },
            { label: 'Critical Missing', value: criticalMissing, icon: XCircle, hint: 'Critical severity', tone: 'red' },
            { label: 'Pending Reboot', value: pendingReboot, icon: RotateCcw, hint: 'Awaiting reboot', tone: 'amber' },
            { label: 'Failed Installations', value: failedInstalls, icon: XCircle, hint: 'From deployment jobs', tone: 'red' },
            { label: 'Patch Compliance', value: `${compliancePct}%`, icon: CheckCircle2, hint: compliancePct >= 90 ? 'Healthy' : 'Needs attention', tone: compliancePct >= 90 ? 'green' : 'amber' },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className={`pm-kpi pm-kpi-${k.tone}`}>
                <div className="pm-kpi-top">
                  <Icon size={18} />
                  <span className="pm-kpi-value">{k.value}</span>
                </div>
                <div className="pm-kpi-label">{k.label}</div>
                <div className="pm-kpi-hint">{k.hint}</div>
              </div>
            );
          })}
        </div>

        {/* Lifecycle stepper */}
        <div className="pm-stepper">
          {stepper.map((step, idx) => (
            <React.Fragment key={step.label}>
              <div className={`pm-step ${step.tone || ''}`}>
                <div className="pm-step-num">{idx + 1}</div>
                <div>
                  <div className="pm-step-label">{step.label}</div>
                  <div className="pm-step-status">{step.status}</div>
                </div>
              </div>
              {idx < stepper.length - 1 && <div className="pm-step-line" />}
            </React.Fragment>
          ))}
        </div>

        {/* Compact OS filter */}
        <div className="pm-os-pills">
          {OS_PILLS.map((pill) => {
            const enabled = pill.id === 'all' || osEnabled(pill.id);
            const count = osCounts[pill.id];
            const Icon = pill.icon;
            return (
              <button
                key={pill.id}
                type="button"
                className={`pm-os-pill ${osFilter === pill.id ? 'active' : ''} ${!enabled ? 'disabled' : ''}`}
                disabled={!enabled || (pill.id !== 'all' && count === 0 && !osEnabled(pill.id))}
                onClick={() => setOsFilter(pill.id)}
                title={!enabled ? 'OS not entitled for this tenant' : undefined}
              >
                <Icon size={14} />
                {pill.label}
                <span className="pm-os-pill-count">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Tabs */}
        <HubPanel withTabs padded={false} className="mt-4">
          <HubTabs
            value={activeTab}
            onChange={setActiveTab}
            items={TABS.map((t) => ({
              id: t.id,
              label: t.label,
              icon: <t.icon size={15} />,
            }))}
          />
          <div className="hub-panel__inner">

        {/* ===== OVERVIEW ===== */}
        {activeTab === 'overview' && (
          <div className="pm-panel-grid">
            <section className="pm-panel">
              <div className="pm-panel-head">
                <h3>Top Critical Required Patches</h3>
                <button type="button" className="pm-link-btn" onClick={() => setActiveTab('required')}>View all</button>
              </div>
              {filteredRequired.length === 0 ? (
                <EmptyState title="All endpoints are currently patch compliant." body="No missing patches for the selected OS filter." />
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Patch</th>
                        <th>Severity</th>
                        <th>OS</th>
                        <th>Devices</th>
                        <th>Approval</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredRequired]
                        .sort((a, b) => Number(isCritical(b.severity)) - Number(isCritical(a.severity)))
                        .slice(0, 8)
                        .map((r) => (
                          <tr key={r.patch_id}>
                            <td className="pm-strong">{r.kb_id || r.package_name || r.patch_id}</td>
                            <td><SeverityBadge severity={r.severity} /></td>
                            <td>{sourceToOs(r.source)}</td>
                            <td>{r.affected_devices ?? 0}</td>
                            <td><ApprovalBadge status={r.approval_status} /></td>
                            <td>
                              <button type="button" className="pm-link-btn" onClick={() => openAffectedDevices(r)}>View</button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="pm-panel">
              <div className="pm-panel-head">
                <h3>Top Devices Needing Attention</h3>
                <button type="button" className="pm-link-btn" onClick={() => setActiveTab('devices')}>View all</button>
              </div>
              {devicesMissingCount === 0 ? (
                <EmptyState title="No devices need patching." body="Fleet looks healthy for the current filter." />
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>OS</th>
                        <th>Missing</th>
                        <th>Critical</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDevices
                        .map((d) => ({ d, r: deviceRollup[d.device_id] || { missing: 0, critical: 0, failed: 0, pendingReboot: 0 } }))
                        .filter(({ r }) => r.missing > 0 || r.pendingReboot > 0 || r.failed > 0)
                        .sort((a, b) => b.r.critical - a.r.critical || b.r.missing - a.r.missing)
                        .slice(0, 8)
                        .map(({ d, r }) => (
                          <tr key={d.device_id}>
                            <td className="pm-strong"><DeviceIdentityCell device={d} fallbackId={d.device_id} /></td>
                            <td>{classifyOs(d.os)}</td>
                            <td>{r.missing}</td>
                            <td>{r.critical}</td>
                            <td>
                              <button
                                type="button"
                                className="pm-link-btn"
                                onClick={() => {
                                  setSelectedDeviceDetail(d);
                                  setActiveTab('devices');
                                }}
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="pm-panel pm-panel-wide">
              <div className="pm-panel-head">
                <h3>Recent Deployment Activity</h3>
                <button type="button" className="pm-link-btn" onClick={() => setActiveTab('deployments')}>View all</button>
              </div>
              {deployments.length === 0 ? (
                <EmptyState title="No patch deployments yet." body="Select required patches and create a deployment." />
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>OS</th>
                        <th>Status</th>
                        <th>Installed</th>
                        <th>Failed</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deployments.slice(0, 6).map((j) => (
                        <tr key={j.id}>
                          <td className="pm-strong">{j.name || j.id}</td>
                          <td>{j.platform || '—'}</td>
                          <td><StatusBadge status={j.status} /></td>
                          <td>{j.installed_count ?? 0}</td>
                          <td>{j.failed_count ?? 0}</td>
                          <td>{j.started_at || j.created_at ? new Date(j.started_at || j.created_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {/* ===== REQUIRED ===== */}
        {activeTab === 'required' && (
          <section className="pm-panel">
            <div className="pm-panel-head">
              <div>
                <h3>Required Patches</h3>
                <p className="pm-muted">Missing patches from device_patch_status (canonical).</p>
              </div>
              <div className="pm-bulk-actions">
                <button type="button" className="pm-btn pm-btn-ghost" disabled={!selectedPatchIds.size || !canApprove} onClick={handleBulkApprove}>
                  <Check size={14} /> Approve Selected
                </button>
                <button type="button" className="pm-btn pm-btn-primary" disabled={!selectedPatchIds.size || !canInstall} onClick={handleBulkDeploy}>
                  <Play size={14} /> Deploy Selected
                </button>
                <button type="button" className="pm-btn pm-btn-ghost" disabled={!selectedPatchIds.size} onClick={() => showToast('Schedule Deployment: select maintenance window in Settings, then Deploy Selected.')}>
                  <Clock size={14} /> Schedule Deployment
                </button>
                <button
                  type="button"
                  className="pm-btn pm-btn-ghost"
                  onClick={() => {
                    const rows = filteredRequired.map((r) => [r.kb_id || r.package_name, r.severity, r.source, r.affected_devices, r.approval_status].join(','));
                    const blob = new Blob([['patch,severity,source,devices,approval', ...rows].join('\n')], { type: 'text/csv' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = 'required-patches.csv';
                    a.click();
                  }}
                >
                  <Download size={14} /> Export
                </button>
              </div>
            </div>
            {filteredRequired.length === 0 ? (
              <EmptyState
                title="No required patches found."
                body="Run scan or refresh device telemetry. Ensure the UEM agent patch monitor is enabled."
              />
            ) : (
              <div className="pm-table-wrap pm-table-sticky">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedPatchIds.size > 0 && selectedPatchIds.size === filteredRequired.length}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedPatchIds(new Set(filteredRequired.map((r) => r.patch_id)));
                            else setSelectedPatchIds(new Set());
                          }}
                        />
                      </th>
                      <th>Patch / KB / Package</th>
                      <th>Severity</th>
                      <th>OS</th>
                      <th>Source</th>
                      <th>Affected</th>
                      <th>Approval</th>
                      <th>Reboot</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequired.map((r) => (
                      <tr key={r.patch_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPatchIds.has(r.patch_id)}
                            onChange={(e) => {
                              const next = new Set(selectedPatchIds);
                              if (e.target.checked) next.add(r.patch_id);
                              else next.delete(r.patch_id);
                              setSelectedPatchIds(next);
                            }}
                          />
                        </td>
                        <td className="pm-strong">
                          {r.kb_id || r.package_name || r.patch_id}
                          {r.version ? <span className="pm-muted"> · {r.version}</span> : null}
                        </td>
                        <td><SeverityBadge severity={r.severity} /></td>
                        <td>{sourceToOs(r.source)}</td>
                        <td>{r.source || '—'}</td>
                        <td>{r.affected_devices ?? 0}</td>
                        <td><ApprovalBadge status={r.approval_status} /></td>
                        <td>{isCritical(r.severity) ? <span className="pm-badge pm-badge-warn">Likely</span> : '—'}</td>
                        <td>
                          <div className="pm-row-actions">
                            <button type="button" className="pm-icon-btn" title="View Devices" onClick={() => openAffectedDevices(r)}><Eye size={14} /></button>
                            <button type="button" className="pm-icon-btn" title="Approve" disabled={!canApprove || busyPatchId === r.patch_id} onClick={() => handleApprove(r.patch_id, 'Approved')}><Check size={14} /></button>
                            <button type="button" className="pm-icon-btn" title="Decline" disabled={!canApprove} onClick={() => handleApprove(r.patch_id, 'Declined')}><X size={14} /></button>
                            <button type="button" className="pm-icon-btn pm-icon-primary" title="Deploy" disabled={!canInstall || busyPatchId === r.patch_id} onClick={() => handleDeploy(r.patch_id)}><Play size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ===== DEVICES ===== */}
        {activeTab === 'devices' && (
          <div className="pm-split">
            <section className="pm-panel">
              <div className="pm-panel-head">
                <h3>Devices Needing Patch</h3>
              </div>
              {filteredDevices.length === 0 ? (
                <EmptyState title="No patch scan data received yet." body="Ensure the UEM agent patch monitor is enabled." />
              ) : (
                <div className="pm-table-wrap pm-table-sticky">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>OS</th>
                        <th>Missing</th>
                        <th>Critical</th>
                        <th>Failed</th>
                        <th>Pending Reboot</th>
                        <th>Last Scan</th>
                        <th>Compliance</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDevices.map((d) => {
                        const r = deviceRollup[d.device_id] || { missing: 0, critical: 0, failed: 0, pendingReboot: 0 };
                        const reports = Array.isArray(d.device_metadata?.patch_reports) ? d.device_metadata.patch_reports.length : 0;
                        const missing = r.missing || reports;
                        const comp = missing === 0 ? 100 : Math.max(0, 100 - missing * 5);
                        return (
                          <tr key={d.device_id} className={selectedDeviceDetail?.device_id === d.device_id ? 'selected' : ''}>
                            <td className="pm-strong"><DeviceIdentityCell device={d} fallbackId={d.device_id} /></td>
                            <td>{classifyOs(d.os)}</td>
                            <td>{missing}</td>
                            <td>{r.critical}</td>
                            <td>{r.failed}</td>
                            <td>{r.pendingReboot}</td>
                            <td>{r.lastScan ? new Date(r.lastScan).toLocaleString() : 'Never scanned'}</td>
                            <td>{comp}%</td>
                            <td>
                              <button type="button" className="pm-link-btn" onClick={() => setSelectedDeviceDetail(d)}>View Missing</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="pm-panel pm-side">
              {!selectedDeviceDetail ? (
                <EmptyState title="Select a device" body="Choose a device to view missing patches and deploy approved updates." />
              ) : (
                <>
                  <div className="pm-panel-head">
                    <h3><DeviceIdentityCell device={selectedDeviceDetail} fallbackId={selectedDeviceDetail.device_id} /></h3>
                  </div>
                  {deviceStatusLoading ? (
                    <p className="pm-muted">Loading status…</p>
                  ) : deviceStatusItems.filter((r) => r.status !== 'Installed' && r.status !== 'Not_Applicable').length === 0 ? (
                    <EmptyState title="No missing / pending patches" body="This device has no actionable patch status rows." />
                  ) : (
                    <div className="pm-table-wrap">
                      <table className="pm-table">
                        <thead>
                          <tr>
                            <th>Patch</th>
                            <th>Status</th>
                            <th>Severity</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceStatusItems
                            .filter((r) => r.status !== 'Installed' && r.status !== 'Not_Applicable')
                            .map((row) => (
                            <tr key={row.status_id || row.patch_id}>
                              <td>
                                <div className="font-medium">{row.patch?.kb_id || row.patch?.package_name || row.patch_id}</div>
                                {row.patch?.package_name && row.patch?.kb_id ? (
                                  <div className="text-xs text-slate-500 truncate max-w-[220px]" title={row.patch.package_name}>
                                    {row.patch.package_name}
                                  </div>
                                ) : null}
                              </td>
                              <td><StatusBadge status={row.status} /></td>
                              <td><SeverityBadge severity={row.patch?.severity} /></td>
                              <td>
                                {row.status === 'Missing' && (
                                  <button
                                    type="button"
                                    className="pm-btn pm-btn-primary pm-btn-sm"
                                    disabled={!canInstall}
                                    onClick={() => handleDeploy(row.patch_id, [selectedDeviceDetail.device_id])}
                                  >
                                    Deploy
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="pm-side-actions">
                    <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setActiveTab('reboot')}>Reboot Status</button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}

        {/* ===== DEPLOYMENTS ===== */}
        {activeTab === 'deployments' && (
          <section className="pm-panel">
            <div className="pm-panel-head">
              <div>
                <h3>Patch Deployments</h3>
                <p className="pm-muted">Tracked jobs from patch installs (confirmation-aware).</p>
              </div>
            </div>
            {deployments.length === 0 ? (
              <EmptyState
                title="No patch deployments yet."
                body="Select required patches and create a deployment. Detailed patch deployment jobs appear after installs are queued."
              />
            ) : (
              <div className="pm-table-wrap pm-table-sticky">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>OS</th>
                      <th>Targets</th>
                      <th>Status</th>
                      <th>Installed</th>
                      <th>Failed</th>
                      <th>Pending Reboot</th>
                      <th>Created By</th>
                      <th>Started</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map((j) => (
                      <tr key={j.id}>
                        <td className="pm-strong" title={j.id}>{j.name || j.id}</td>
                        <td>{j.platform || '—'}</td>
                        <td>{j.target_count ?? 0}</td>
                        <td><StatusBadge status={j.status} /></td>
                        <td>{j.installed_count ?? 0}</td>
                        <td>{j.failed_count ?? 0}</td>
                        <td>{j.pending_reboot_count ?? 0}</td>
                        <td>{j.created_by || '—'}</td>
                        <td>{j.started_at || j.created_at ? new Date(j.started_at || j.created_at).toLocaleString() : '—'}</td>
                        <td>{j.completed_at ? new Date(j.completed_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ===== REBOOT ===== */}
        {activeTab === 'reboot' && (
          <div className="space-y-4">
            <div className="pm-kpi-grid pm-kpi-grid-4">
              {[
                { label: 'Pending Reboot', value: pendingReboot, tone: 'amber' },
                { label: 'Policies', value: rebootPolicies.length, tone: 'blue' },
                { label: 'Prompt Policies', value: rebootPolicies.filter((p) => p.type === 'Prompt').length, tone: 'green' },
                { label: 'Force Policies', value: rebootPolicies.filter((p) => p.type === 'Force').length, tone: 'red' },
              ].map((k) => (
                <div key={k.label} className={`pm-kpi pm-kpi-${k.tone}`}>
                  <div className="pm-kpi-value">{k.value}</div>
                  <div className="pm-kpi-label">{k.label}</div>
                </div>
              ))}
            </div>
            <section className="pm-panel">
              <div className="pm-panel-head">
                <h3>Pending Reboot Devices</h3>
                <div className="pm-bulk-actions">
                  <button type="button" className="pm-btn pm-btn-ghost" onClick={() => loadAll(true)}><RefreshCw size={14} /> Refresh</button>
                  <button type="button" className="pm-btn pm-btn-ghost" disabled={!pendingReboot || !canRebootManage} onClick={async () => { await apiService.triggerRebootRemind(); showToast('Remind sent.'); loadAll(true); }}>
                    <Bell size={14} /> Send Remind
                  </button>
                  <button type="button" className="pm-btn pm-btn-primary" disabled={!canRebootManage} onClick={() => setPolicyModalOpen(true)}>
                    <Plus size={14} /> Create Policy
                  </button>
                </div>
              </div>
              {(rebootSummary?.items || []).length === 0 ? (
                <EmptyState title="No devices pending reboot." body="Devices appear here after patch installs that require a restart." />
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rebootSummary?.items || []).map((item: any, idx: number) => {
                        const dev = devices.find((d) => d.device_id === item.device_id);
                        const identity = dev || item;
                        return (
                          <tr key={item.device_id || idx}>
                            <td className="pm-strong">
                              <DeviceIdentityCell device={identity} fallbackId={item.device_id} />
                            </td>
                            <td>{item.reason || 'patch_install'}</td>
                            <td>
                              <button type="button" className="pm-link-btn" disabled={!canForceReboot} title={!canForceReboot ? 'Force reboot permission required' : undefined}>
                                Force / Schedule
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {rebootPolicies.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Reboot Policies</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {Array.from(
                      new Map(
                        rebootPolicies.map((p: any) => [String(p.policy_id || p.id || `${p.name}:${p.type}`), p])
                      ).values()
                    ).map((p: any) => (
                      <li key={String(p.policy_id || p.id || `${p.name}:${p.type}`)}>
                        {p.name} · {p.type} {p.is_active ? '(active)' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>
        )}

        {/* ===== CATALOG ===== */}
        {activeTab === 'catalog' && (
          <div className="space-y-4">
            <section className="pm-panel">
              <div className="pm-panel-head">
                <div>
                  <h3>Catalog Sync Status</h3>
                  <p className="pm-muted">Microsoft MSRC and related catalog metadata.</p>
                </div>
                <button type="button" className="pm-btn pm-btn-primary" onClick={handleSyncCatalog} disabled={syncing}>
                  <RefreshCw size={14} className={syncing ? 'pm-spin' : ''} /> Sync Now
                </button>
              </div>
              <div className="pm-sync-meta">
                <div><span className="pm-muted">Status</span> <StatusBadge status={syncStatus?.status || 'never'} /></div>
                <div><span className="pm-muted">Patches synced</span> <strong>{syncStatus?.patches_synced ?? 0}</strong></div>
                <div><span className="pm-muted">Last run</span> <strong>{syncStatus?.completed_at || syncStatus?.started_at ? new Date(syncStatus.completed_at || syncStatus.started_at).toLocaleString() : '—'}</strong></div>
                <div><span className="pm-muted">Months</span> <strong>{(syncStatus?.months_synced || []).join(', ') || '—'}</strong></div>
              </div>
            </section>

            <section className="pm-panel">
              <div className="pm-panel-head">
                <h3>Patch Master Catalog</h3>
              </div>
              <div className="pm-filters">
                <select value={patchMasterSource} onChange={(e) => setPatchMasterSource(e.target.value)}>
                  <option value="msrc">Windows (MSRC)</option>
                  <option value="apt">Linux (apt)</option>
                  <option value="yum">Linux (yum)</option>
                  <option value="apple">macOS (Apple)</option>
                </select>
                <select value={patchMasterSeverity} onChange={(e) => setPatchMasterSeverity(e.target.value)}>
                  <option value="">All severity</option>
                  <option value="Critical">Critical</option>
                  <option value="Important">Important</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Low">Low</option>
                </select>
                <select value={patchMasterYear} onChange={(e) => setPatchMasterYear(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Year</option>
                  {[0, 1, 2].map((i) => {
                    const y = new Date().getFullYear() - i;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
                <select value={patchMasterMonth} onChange={(e) => setPatchMasterMonth(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <input
                  className="pm-search"
                  placeholder="Search KB, package, CVE…"
                  value={patchMasterSearch}
                  onChange={(e) => setPatchMasterSearch(e.target.value)}
                />
                <button type="button" className="pm-btn pm-btn-primary" onClick={loadPatchMaster} disabled={patchMasterLoading}>
                  {patchMasterLoading ? 'Loading…' : 'Load Catalog'}
                </button>
              </div>
              {patchMasterError && <div className="pm-error-banner">{patchMasterError}</div>}
              {!catalogItems.length ? (
                <EmptyState title="Patch catalog is empty." body="Run Sync Catalog to import patch metadata, then Load Catalog." />
              ) : (
                <div className="pm-table-wrap pm-table-sticky">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Patch</th>
                        <th>Severity</th>
                        <th>Source</th>
                        <th>Release</th>
                        <th>CVEs</th>
                        <th>Installable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogItems.map((p: any) => {
                        const installable = isUuid(p.patch_id) && !String(p.patch_id).startsWith('msrc-');
                        return (
                          <tr key={p.patch_id || p.kb_id}>
                            <td className="pm-strong">{p.kb_id || p.package_name || p.patch_id}</td>
                            <td><SeverityBadge severity={p.severity} /></td>
                            <td>{p.source || '—'}</td>
                            <td>{p.release_date ? new Date(p.release_date).toLocaleDateString() : '—'}</td>
                            <td className="pm-muted">{(p.cve_ids || []).slice(0, 3).join(', ') || '—'}</td>
                            <td>
                              {installable ? (
                                <span className="pm-badge pm-badge-ok">Installable</span>
                              ) : (
                                <span className="pm-badge pm-badge-neutral" title="Catalog-only / ephemeral — not in patch_master UUID form">Catalog only</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {/* ===== SETTINGS ===== */}
        {activeTab === 'settings' && (
          <section className="pm-panel">
            <div className="pm-panel-head">
              <div>
                <h3>Patch Settings</h3>
                <p className="pm-muted">Tenant approval, auto-approve, maintenance window, and reboot policy preferences.</p>
              </div>
            </div>
            <PatchSettings />
          </section>
        )}

          </div>
        </HubPanel>

        {/* Affected devices drawer/modal */}
        {drawerPatch && (
          <div className="pm-drawer-backdrop" onClick={() => setDrawerPatch(null)}>
            <div className="pm-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="pm-drawer-head">
                <div>
                  <h3>{drawerPatch.kb_id || drawerPatch.package_name || drawerPatch.patch_id}</h3>
                  <div className="pm-drawer-meta">
                    <SeverityBadge severity={drawerPatch.severity} />
                    <span className="pm-muted">{drawerPatch.source}</span>
                    <ApprovalBadge status={drawerPatch.approval_status} />
                  </div>
                </div>
                <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setDrawerPatch(null)}>Close</button>
              </div>
              {drawerLoading ? (
                <p className="pm-muted p-4">Loading affected devices…</p>
              ) : drawerDevices.length === 0 ? (
                <EmptyState title="No matching device status rows found." body="Devices may not have scanned this patch yet. Refresh after agent scan." />
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>OS</th>
                        <th>Status</th>
                        <th>Last scan</th>
                        <th>Reboot</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawerDevices.map((row) => (
                        <tr key={row.device.device_id}>
                          <td className="pm-strong"><DeviceIdentityCell device={row.device} fallbackId={row.device?.device_id} /></td>
                          <td>{classifyOs(row.device.os)}</td>
                          <td><StatusBadge status={row.status} /></td>
                          <td>{row.lastScan ? new Date(row.lastScan).toLocaleString() : '—'}</td>
                          <td>{row.reboot ? 'Yes' : 'No'}</td>
                          <td>
                            <button
                              type="button"
                              className="pm-link-btn"
                              onClick={() => {
                                setSelectedDeviceDetail(row.device);
                                setDrawerPatch(null);
                                setActiveTab('devices');
                              }}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="pm-drawer-foot">
                <button
                  type="button"
                  className="pm-btn pm-btn-primary"
                  disabled={!canInstall || !drawerDevices.length}
                  onClick={() => handleDeploy(drawerPatch.patch_id, drawerDevices.map((r) => r.device.device_id))}
                >
                  Deploy to listed devices
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create reboot policy modal */}
        {policyModalOpen && (
          <div className="pm-drawer-backdrop" onClick={() => setPolicyModalOpen(false)}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-semibold text-slate-800 mb-3">Create Reboot Policy</h3>
              <label className="block text-sm mb-2">Name
                <input className="pm-search w-full mt-1" value={policyForm.name} onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })} />
              </label>
              <label className="block text-sm mb-3">Type
                <select className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2" value={policyForm.type} onChange={(e) => setPolicyForm({ ...policyForm, type: e.target.value })}>
                  <option value="Prompt">Prompt</option>
                  <option value="Force">Force</option>
                  <option value="Suppress">Suppress</option>
                  <option value="Schedule">Schedule</option>
                </select>
              </label>
              <div className="flex gap-2 justify-end">
                <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setPolicyModalOpen(false)}>Cancel</button>
                <button type="button" className="pm-btn pm-btn-primary" disabled={!policyForm.name || policySubmitLoading} onClick={handleCreatePolicy}>
                  {policySubmitLoading ? 'Saving…' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default PatchManagement;
